/**
 * RBAC-4.3 · Users → Access removed. People whose access was removed (`remove_person_access`) are
 * inactive but not deleted, and the Users list reads active people only, so without this they would
 * be on no screen and a removal could not be undone. Restore is `restore_person_access`, which asks
 * RBAC-1.5 of every role they come back with; its refusal is shown as it comes.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { restoreAccessErrorMessage, type RemovedPerson } from '@/lib/personAccess';
import { PersonAccessError, fetchRemovedPeople, restorePersonAccess } from '@/services/personAccessService';

export interface RemovedPeopleSectionProps {
  /** Bumped by the parent after a removal, so the list follows. */
  refreshKey: number;
  onCount?: (n: number) => void;
  onRestored?: () => void;
}

export function RemovedPeopleSection({ refreshKey, onCount, onRestored }: RemovedPeopleSectionProps) {
  const [people, setPeople] = useState<RemovedPerson[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const rows = await fetchRemovedPeople();
      setPeople(rows);
      onCount?.(rows.length);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'The list could not be read.');
    }
  }, [onCount]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const restore = async (p: RemovedPerson) => {
    setBusy(p.userId);
    setErrors((prev) => ({ ...prev, [p.userId]: '' }));
    try {
      await restorePersonAccess(p.userId);
      await load();
      onRestored?.();
    } catch (e) {
      const rpc = e instanceof PersonAccessError ? e.rpc : { message: e instanceof Error ? e.message : undefined };
      setErrors((prev) => ({ ...prev, [p.userId]: restoreAccessErrorMessage(rpc) }));
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <p role="alert" className="p-4 text-sm text-destructive">
        {loadError}
      </p>
    );
  }
  if (!people) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4" data-testid="removed-people">
      <p className="text-sm text-muted-foreground">
        Their records are kept. Restoring lets them sign in again with the role they had; they are not signed back in
        automatically.
      </p>
      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody's access has been removed.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {people.map((p) => (
            <li key={p.userId} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid="removed-person">
              <div className="min-w-0">
                <p className="font-medium">{[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || 'Unnamed'}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {[p.email, p.roleName].filter(Boolean).join(' · ')}
                </p>
                {errors[p.userId] && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors[p.userId]}
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => void restore(p)} disabled={busy !== null}>
                {busy === p.userId ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1.5 h-4 w-4" />}
                Restore access
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
